import type {
  ChecklistCanvasNode,
  ChecklistCanvasNodeWritePatch,
  ChecklistCanvasPosition,
} from '../types/checklistCanvas'
import type {
  ChecklistItem,
  ChecklistNodeKind,
  ChecklistNodeLink,
  TimelineBoardKey,
} from '../types/schema'

const DEFAULT_POSITION_SPACING = 96

type LegacyChecklistTitle = {
  text?: unknown
  sourceChecklistId?: unknown
}

export type ChecklistCanvasProjectionInput = Partial<ChecklistItem> & LegacyChecklistTitle & {
  id: string
}

export type ChecklistCanvasProjectionOptions = {
  index?: number
  fallbackWorkspaceId?: string
  fallbackParentContainerId?: string
  fallbackBoard?: TimelineBoardKey
  layoutVersion?: number
}

const isTimelineBoardKey = (value: unknown): value is TimelineBoardKey =>
  value === 'VISIONBOARD' || value === 'DASHBOARD' || value === 'ARCHIVE' || value === 'BIN'

const normalizeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((entry): entry is string => typeof entry === 'string')
}

const normalizeNodeKind = (value: unknown): ChecklistNodeKind => {
  if (value === 'GATE_AND' || value === 'GATE_OR') {
    return value
  }

  return 'CHECKLIST'
}

const normalizePosition = (value: unknown, index = 0): ChecklistCanvasPosition => {
  if (
    value &&
    typeof value === 'object' &&
    typeof (value as ChecklistCanvasPosition).x === 'number' &&
    typeof (value as ChecklistCanvasPosition).y === 'number'
  ) {
    return {
      x: (value as ChecklistCanvasPosition).x,
      y: (value as ChecklistCanvasPosition).y,
    }
  }

  return {
    x: 0,
    y: index * DEFAULT_POSITION_SPACING,
  }
}

const normalizeChecklistNodeLinks = (value: unknown): ChecklistNodeLink[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return []
    }

    const link = entry as Partial<ChecklistNodeLink>
    if (typeof link.targetId !== 'string') {
      return []
    }

    return [{
      targetId: link.targetId,
      targetType: 'CHECKLIST',
      title: typeof link.title === 'string' ? link.title : '',
    }]
  })
}

const normalizeTitle = (item: ChecklistCanvasProjectionInput): string => {
  if (typeof item.title === 'string' && item.title.trim()) {
    return item.title
  }

  if (typeof item.text === 'string' && item.text.trim()) {
    return item.text
  }

  return 'Untitled checklist'
}

export const checklistItemToCanvasNode = (
  item: ChecklistCanvasProjectionInput,
  options: ChecklistCanvasProjectionOptions = {},
): ChecklistCanvasNode => {
  const nodeKind = normalizeNodeKind(item.nodeKind)
  const board = isTimelineBoardKey(item.board) ? item.board : options.fallbackBoard ?? 'DASHBOARD'

  return {
    id: item.id,
    workspaceId: item.workspaceId ?? options.fallbackWorkspaceId ?? '',
    parentContainerId: item.parentContainerId ?? options.fallbackParentContainerId ?? '',
    board,
    nodeKind,
    position: normalizePosition(item.position, options.index),
    workspaceCanvasLayoutVersion: item.workspaceCanvasLayoutVersion ?? options.layoutVersion ?? 1,
    detailItemId: nodeKind === 'CHECKLIST' ? item.id : null,
    legacyChecklistItemId: item.id,
    sourceChecklistId: typeof item.sourceChecklistId === 'string' ? item.sourceChecklistId : null,
    checklistType: item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY' || item.checklistType === 'INDIVIDUAL'
      ? item.checklistType
      : undefined,
    prerequisites: normalizeStringArray(item.prerequisites),
    dependencyNodeIds: normalizeStringArray(item.dependencyNodeIds),
    blockedByNodeIds: normalizeChecklistNodeLinks(item.blockedByNodeIds),
    isBlockerForNodeIds: normalizeChecklistNodeLinks(item.isBlockerForNodeIds),
    gateInputIds: normalizeStringArray(item.gateInputIds),
    display: {
      title: normalizeTitle(item),
      isCompleted: item.isCompleted === true,
      workflowStatus: item.workflowStatus ?? null,
      boardStatus: item.boardStatus ?? null,
      weightage: typeof item.weightage === 'number' ? item.weightage : 0,
      assigneeId: item.assigneeId ?? null,
      assigneeName: item.assigneeName ?? null,
      isHelperVisible: item.isHelperVisible,
      complexityStars: item.complexityStars,
      isUrgentCrisisMode: item.isUrgentCrisisMode,
      countdownHours: item.countdownHours ?? null,
      timerStartedAt: item.timerStartedAt ?? null,
      timerExpiredAt: item.timerExpiredAt ?? null,
      isReady: item.isReady,
      resourceReadySku: item.resourceReadySku ?? null,
    },
    createdById: item.createdById,
    createdByName: item.createdByName,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    lastUpdatedBy: item.lastUpdatedBy ?? null,
    lastUpdatedByName: item.lastUpdatedByName ?? null,
  }
}

export const checklistCanvasNodeToWritePatch = (
  node: Pick<
    ChecklistCanvasNode,
    | 'position'
    | 'workspaceCanvasLayoutVersion'
    | 'prerequisites'
    | 'dependencyNodeIds'
    | 'blockedByNodeIds'
    | 'isBlockerForNodeIds'
    | 'gateInputIds'
    | 'updatedAt'
    | 'lastUpdatedBy'
    | 'lastUpdatedByName'
  >,
): ChecklistCanvasNodeWritePatch => ({
  position: node.position,
  workspaceCanvasLayoutVersion: node.workspaceCanvasLayoutVersion,
  prerequisites: node.prerequisites,
  dependencyNodeIds: node.dependencyNodeIds,
  blockedByNodeIds: node.blockedByNodeIds,
  isBlockerForNodeIds: node.isBlockerForNodeIds,
  gateInputIds: node.gateInputIds,
  updatedAt: node.updatedAt,
  lastUpdatedBy: node.lastUpdatedBy,
  lastUpdatedByName: node.lastUpdatedByName,
})

export const checklistCanvasNodeToChecklistItemProjection = (
  node: ChecklistCanvasNode,
): Partial<ChecklistItem> & { sourceChecklistId?: unknown } => ({
  id: node.legacyChecklistItemId ?? node.detailItemId ?? node.id,
  parentContainerId: node.parentContainerId,
  workspaceId: node.workspaceId,
  board: node.board,
  title: node.display.title,
  boardStatus: node.display.boardStatus ?? null,
  workflowStatus: node.display.workflowStatus ?? null,
  isCompleted: node.display.isCompleted,
  weightage: node.display.weightage,
  assigneeId: node.display.assigneeId ?? null,
  assigneeName: node.display.assigneeName ?? null,
  isHelperVisible: node.display.isHelperVisible,
  complexityStars: node.display.complexityStars,
  isUrgentCrisisMode: node.display.isUrgentCrisisMode,
  countdownHours: node.display.countdownHours ?? null,
  timerStartedAt: node.display.timerStartedAt ?? null,
  timerExpiredAt: node.display.timerExpiredAt ?? null,
  isReady: node.display.isReady,
  resourceReadySku: node.display.resourceReadySku ?? null,
  position: node.position,
  workspaceCanvasLayoutVersion: node.workspaceCanvasLayoutVersion,
  prerequisites: node.prerequisites,
  dependencyNodeIds: node.dependencyNodeIds,
  blockedByNodeIds: node.blockedByNodeIds,
  isBlockerForNodeIds: node.isBlockerForNodeIds,
  gateInputIds: node.gateInputIds,
  nodeKind: node.nodeKind,
  checklistType: node.checklistType,
  sourceChecklistId: node.sourceChecklistId ?? node.detailItemId ?? node.legacyChecklistItemId ?? node.id,
  createdById: node.createdById,
  createdByName: node.createdByName,
  createdAt: node.createdAt,
  updatedAt: node.updatedAt,
  lastUpdatedBy: node.lastUpdatedBy ?? null,
  lastUpdatedByName: node.lastUpdatedByName ?? null,
})
